import getStore from "src/utils/redux/store";
import { isInsideNoomWebView, pushReroll, setParam } from "./utils";
import { OffersParams } from "@pageDefinitions/goto/offers";
import {
  getNoomSessionStorage,
  setNoomSessionStorage,
} from "src/utils/noomSessionStorage";
import { SessionStorageKey } from "src/utils/constants";
import { createForwardParams, getSearchQuery } from "src/utils/urlParams";

import { reroll } from "../components/PageSetRouter";
import { isTelehealth } from "src/utils/userSegment/features";
import goto from "@pageDefinitions/goto/index";
import {
  membershipPurchasedLayer,
  telehealthFeedbackLayer,
} from "@pageDefinitions/goto/constants";
import { DEFAULT_APP_LOGIN_DEEPLINK } from "src/utils/in-app/deeplinkHandler";
import { hasInAppSurveyModeQueryParam } from "src/components/refactored-survey/question-sets/insurance-survey-questions/utils/surveyFlowLogic";
import telehealthNativeAppTasks from "src/components/refactored-survey/question-sets/insurance-survey-questions/utils/telehealthNativeAppTasks";
import { captureMessage } from "@sentry/browser";
import { getB2BReturnUrl } from "src/components/refactored-survey/question-sets/insurance-survey-questions/utils/getB2BReturnUrl";
import { postMakeEligible } from "src/funnels/insurance-reimbursement/requests/wheelRequestsDeprecated/postMakeEligible";
import { appConfig } from "src/config";
import { isMembershipProductName } from "src/utils/membership";

/** Catalog product name of the plan this checkout sold. */
function getCheckoutPlanProductName() {
  return getStore().getState().recommendedPlan?.product_name;
}

/** True only when the plan this checkout sold is the membership plan. */
function purchasedMembershipPlan() {
  return isMembershipProductName(getCheckoutPlanProductName());
}

function telehealthPath(
  pathname: string,
  additionalQueryParams?: Record<string, string | number | boolean>
) {
  const store = getStore();
  const state = store.getState();
  const { userData } = state;

  const queryParams = createForwardParams();
  setParam(queryParams, "userId", userData.user_id);

  // eslint-disable-next-line no-restricted-syntax,guard-for-in
  for (const key in additionalQueryParams) {
    if (additionalQueryParams[key]) {
      setParam(queryParams, key, additionalQueryParams[key]);
    }
  }

  return `/telehealth${pathname}${getSearchQuery(queryParams)}`;
}
const telehealthRoot = (
  additionalQueryParams?: Record<string, string | number | boolean>
) => {
  return telehealthPath("/", additionalQueryParams);
};

export default {
  async insurance(isTelehealthBuyflow: boolean) {
    const state = getStore().getState();

    if (isTelehealthBuyflow) {
      setNoomSessionStorage(SessionStorageKey.isTelehealthBuyflow, true);
      await postMakeEligible(state.userData.user_id);
    } else {
      setNoomSessionStorage(SessionStorageKey.isTelehealthBuyflow, false);
    }

    const additionalQueryParams = {
      noomPlanId: state.recommendedPlan.noom_plan_id,
    };
    reroll(
      `${telehealthRoot(additionalQueryParams)}#/insurance`,
      undefined,
      !isTelehealthBuyflow
    );
  },

  patientIntake(params?: OffersParams) {
    const additionalQueryParams = params ? { userId: params.userId } : {};
    pushReroll(
      `${telehealthRoot(additionalQueryParams)}#/patientIntake`,
      isTelehealth
    );
  },

  // Allow parameters to goto.telehealthLanding to specify goto= for steadymd
  // users.
  telehealthLanding(gotoPath?: string) {
    reroll(
      `${telehealthRoot()}${gotoPath ? `&goto=${gotoPath}` : ""}#/landing`
    );
  },

  telehealthSchedule() {
    const path = "/schedule/initial_video_visit";
    reroll(telehealthPath(path), undefined, true);
  },

  telehealthReschedule() {
    const path = "/reschedule/initial_video_visit";
    reroll(telehealthPath(path), undefined, true);
  },

  telehealthB2BEnrollmentComplete() {
    reroll(`${telehealthRoot()}#/b2bComplete`);
  },

  async feedback(replaceHistory = false) {
    return goto.pushLayer(telehealthFeedbackLayer, null, replaceHistory);
  },

  // SteadyMD surveys

  async steadyIntake(fromNoomMedOffer = false) {
    const params = new URLSearchParams();
    if (fromNoomMedOffer) {
      params.set("fromNoomMedOffer", "true");
    }
    reroll(`${telehealthRoot()}#/steadyPatientIntake`, params, true);
  },

  async whiteLabelPatientIntake(clinicianVendor: string) {
    reroll(`${telehealthRoot({ clinicianVendor })}#/whiteLabelPatientIntake`);
  },

  async telehealthEnrollmentSetup() {
    reroll(`${telehealthRoot()}#/enrollmentSetup`);
  },

  /**
   * Membership purchases enter the membership-purchased layer immediately.
   * Clinical intake follows that layer, then medication selection, then
   * upsells.
   */
  async routeToMembershipPurchased() {
    if (!purchasedMembershipPlan()) {
      return false;
    }

    goto.layer(membershipPurchasedLayer);
    return true;
  },

  /**
   * Opens the Noom app. The app-download page calls this from its button.
   */
  exitTelehealthToApp() {
    const inApp = hasInAppSurveyModeQueryParam() || isInsideNoomWebView();
    if (!inApp) {
      window.location.href = DEFAULT_APP_LOGIN_DEEPLINK;
      return;
    }

    if (
      getNoomSessionStorage(SessionStorageKey.isTelehealthBuyflow) &&
      goto.handleFreemiumComplete()
    ) {
      return;
    }

    if (telehealthNativeAppTasks.webviewExit()) {
      return;
    }

    if (isInsideNoomWebView()) {
      goto.closeInAppWebview();
      return;
    }

    captureMessage("Failure closing the webview after telehealth intake");
    goto.landing();
  },

  async labs() {
    reroll(`${telehealthRoot()}#/labs`, undefined, true);
  },

  async pharmacy() {
    reroll(`${telehealthRoot()}#/pharmacy`, undefined, true);
  },

  // V2 New patient Routes
  async insuranceV2() {
    reroll(`${telehealthRoot()}#/v2/insurance`, undefined, true);
  },

  b2bReturnUrl() {
    const url = getB2BReturnUrl();
    if (url) {
      window.location.href = url;
    }
  },

  patientCenterLabs() {
    window.location.href = `${appConfig.PATIENT_PORTAL_URL}documents`;
  },
};
